import React, { useState, useEffect, useRef } from 'react';
import { getCourseRecommendations, initializeApp } from '../services/api';
import { Search } from 'lucide-react';

const Chat = () => {
  const [userInput, setUserInput] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [thinkingStage, setThinkingStage] = useState('');
  const [serverStatus, setServerStatus] = useState('checking'); // 'checking', 'awake', 'sleeping'

  const scrollRef1 = useRef(null);
  const scrollRef2 = useRef(null);

  const suggestions = [
    "interesting seg classes 💻",
    "how can i become a ceo 💼",
    "i wanna learn japanese 💬",
    "how do i do machine learning 🤖",
    "i want to be a doctor 🩺",
    "how do i master piano 🎹",
    "i want to save the environment 🌍",
    "easy courses for my elective 🥹",
    "i want public speaking skills 🗣️",
    "any game development classes 🎮",
    "how can i become an accountant 💰"
  ];

  // Initialize app and wake up server on mount
  useEffect(() => {
    initializeApp();
    
    // Optional: Show a friendly status indicator
    setTimeout(() => {
      setServerStatus('awake');
    }, 2000);
  }, []);

  useEffect(() => {
    if (loading) {
      setThinkingStage('thinking...');
    } else {
      setThinkingStage('');
    }
  }, [loading]);

  const handleInputChange = (e) => {
    setUserInput(e.target.value);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setUserInput(suggestion);
  };

  const handleSendMessage = async () => {
    if (userInput.trim() === '' || loading) return;

    const currentInput = userInput.trim();
    setLoading(true);
    setResults(null);

    try {
      const response = await getCourseRecommendations(currentInput);
      
      const modelResponse = response.response || response.data?.response;
      
      if (!modelResponse) {
        throw new Error("No response from server");
      }
      
      setResults(modelResponse);
      
    } catch (error) {
      console.error("Error:", error);
      
      // Better error messages
      if (error.message.includes('waking up')) {
        setResults("uoCourses is experiencing some issues.. please try again later :\\");
      } else {
        setResults("uoCourses is experiencing some issues.. please try again later :\\");
      }
    } finally {
      setLoading(false);
      setThinkingStage('');
    }
  };

  const parseCourses = (message) => {
    const coursePattern = /\*\*([A-Z]{3}\d{4})[:\s-]*(.*?)\*\*/g;
    const courses = [];
    const parts = message.split(coursePattern);
    
    for (let i = 1; i < parts.length; i += 3) {
      const code = parts[i];
      const title = parts[i + 1] ? parts[i + 1].trim() : '';
      const content = parts[i + 2] ? parts[i + 2].trim() : '';
      
      const prereqMatch = content.match(/Prerequisites?:\s*(.+?)(?=\s*\*\*|$)/i);
      const prereqs = prereqMatch ? prereqMatch[1].trim() : '';
      
      const description = content.replace(/Prerequisites?:\s*.+$/i, '').trim();
      
      courses.push({
        code,
        title,
        description,
        prereqs
      });
    }

    return courses;
  };

  const formatMessage = (message) => {
    const courses = parseCourses(message);
    
    if (courses.length === 0) {
      return (
        <div className="course-box">
          <p className="course-description">{message}</p>
        </div>
      );
    }

    return courses.map((course, index) => (
      <div key={index} className="course-box">
        <div className="course-code">{course.code}</div>
        {course.title && <h3 className="course-title">{course.title}</h3>}
        {course.description && <p className="course-description">{course.description}</p>}
        {course.prereqs && (
          <div className="course-prereqs">
            Prerequisites: {course.prereqs}
          </div>
        )}
      </div>
    ));
  };

  return (
    <div className="chat-container">
      <div className="chat-main">
        <div className="chat-header">
          <h1>uoCourses</h1>
        </div>

        <div className="suggestions-container">
          <div className="suggestions-scroll-wrapper">
            <div className="suggestions-scroll-content">
              {[...suggestions.slice(0, 5), ...suggestions.slice(0, 5)].map((suggestion, index) => (
                <div 
                  key={index} 
                  className="suggestion-pill"
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  {suggestion}
                </div>
              ))}
            </div>
          </div>
          <div className="suggestions-scroll-wrapper">
            <div className="suggestions-scroll-content suggestions-scroll-reverse">
              {[...suggestions.slice(5), ...suggestions.slice(5)].map((suggestion, index) => (
                <div 
                  key={index} 
                  className="suggestion-pill"
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  {suggestion}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="chat-input-container">
          <div className="input-wrapper">
            <Search className="search-icon" size={18} />
            <input
              type="text"
              value={userInput}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              placeholder="what do u want to learn?"
              className="chat-input"
              disabled={loading}
            />
          </div>
          <button
            onClick={handleSendMessage}
            disabled={loading || !userInput.trim()}
            className="chat-button"
          >
            {loading ? '...' : 'send'}
          </button>
        </div>

        {loading && (
          <div className="loading-container">
            <div className="thinking-text">{thinkingStage}</div>
            <div className="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        )}

        {results && !loading && (
          <div className="courses-container">
            {formatMessage(results)}
          </div>
        )}
      </div>

      <div className="chat-footer">
        made with <span style={{ color: 'red' }}>❤️</span> by mb
      </div>
    </div>
  );
};

export default Chat;